import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { LicenseService } from '../../services/license.service';
import { LogService } from '../../services/log.service';
import { ProductService } from '../../services/product.service';

// Importaciones de interfaces.
import { ILicense } from '../../interfaces/license.interface';
import { IProduct } from '../../interfaces/product.interface';
import { IProductResponse } from '../../interfaces/product-response.interface';

// Importaciones de modelos.
import { License } from '../../models/license.model';
import { Log } from '../../models/log.model';
import { Product } from '../../models/product.model';

/**
 * Componente de diálogo para la actualización de la licencia.
 *
 * Este componente proporciona una interfaz para actualizar la información de una licencia.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos de la licencia seleccionada.
 */
@Component({
  selector: 'app-license-update-dialog',
  templateUrl: './license-update-dialog.component.html',
  standalone: false,
})
export class LicenseUpdateDialogComponent implements OnInit {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de actualización de la licencia.
   */
  private readonly dialogRef: MatDialogRef<LicenseUpdateDialogComponent> =
    inject(MatDialogRef<LicenseUpdateDialogComponent>);

  /**
   * Datos del de la licencia.
   *
   * Contiene la información de la licencia que se está actualizando, obtenida a través de la inyección de dependencias.
   */
  private readonly license: ILicense = inject<ILicense>(MAT_DIALOG_DATA);

  /**
   * Indica si la carga de los productos está en curso.
   *
   * Se utiliza para mostrar un indicador de carga mientras se obtienen los productos disponibles.
   */
  loadingProducts: boolean = false;

  /**
   * Lista de productos disponibles.
   *
   * Contiene los productos que pueden asignarse a la licencia en el proceso de creación.
   */
  products: IProduct[] = [];

  /**
   * Indica si la operación de actualizar está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se actualiza la información de la licencia.
   */
  loadingUpdate: boolean = false;

  /**
   * Lista de errores al actualizar.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización de la licencia.
   *
   * Gestiona los campos y validaciones necesarias para actualizar la información de la licencia.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    id: new FormControl('', Validators.required),
    productId: new FormControl('', Validators.required),
    alias: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(3),
    ]),
    name: new FormControl('', Validators.required),
    type: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(2),
    ]),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'id'.
   *
   * Retorna el control asociado a la propiedad 'id' del formulario,
   * que gestiona la identificación de la licencia.
   *
   * @returns El control de formulario para 'id'.
   */
  get formId(): AbstractControl | null {
    return this.form.get('id');
  }

  /**
   * Obtiene el control del formulario para el campo 'productId'.
   *
   * Retorna el control asociado a la propiedad 'productId' del formulario,
   * que gestiona el producto de la licencia.
   *
   * @returns El control de formulario para 'productId'.
   */
  get formProductId(): AbstractControl | null {
    return this.form.get('productId');
  }

  /**
   * Obtiene el control del formulario para el campo 'alias'.
   *
   * Retorna el control asociado a la propiedad 'alias' del formulario,
   * que gestiona el alias de la licencia.
   *
   * @returns El control de formulario para 'alias'.
   */
  get formAlias(): AbstractControl | null {
    return this.form.get('alias');
  }

  /**
   * Obtiene el control del formulario para el campo 'name'.
   *
   * Retorna el control asociado a la propiedad 'name' del formulario,
   * que gestiona el nombre de la licencia.
   *
   * @returns El control de formulario para 'name'.
   */
  get formName(): AbstractControl | null {
    return this.form.get('name');
  }

  /**
   * Obtiene el control del formulario para el campo 'type'.
   *
   * Retorna el control asociado a la propiedad 'type' del formulario,
   * que gestiona el tipo de licencia.
   *
   * @returns El control de formulario para 'type'.
   */
  get formType(): AbstractControl | null {
    return this.form.get('type');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param licenseService - Servicio para gestionar la información de las licencias.
   * @param logService - Servicio para gestionar los registros.
   * @param productService - Servicio para gestionar los productos de las licencias.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private licenseService: LicenseService,
    private logService: LogService,
    private productService: ProductService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta cuando el componente es inicializado. Es utilizado para realizar tareas
   * de configuración o cargar datos necesarios para la operación del componente.
   */
  async ngOnInit(): Promise<void> {
    this.loadingProducts = true;

    try {
      const productResponse: IProductResponse =
        await this.productService.getAll();
      this.products = productResponse.products.map((product: IProduct) => {
        return new Product(product);
      });
    } finally {
      this.loadingProducts = false;
    }

    this.formProductId?.setValue(this.license.product.id);
    this.formId?.setValue(this.license.id);
    this.formAlias?.setValue(this.license.alias);
    this.formName?.setValue(this.license.name);
    this.formType?.setValue(this.license.type);
    this.formStatus?.setValue(!this.license.disabled);
  }

  /**
   * Actualiza los datos de la licencia.
   *
   * Este método se encarga de realizar el proceso de actualizar la licencia.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async update(): Promise<void> {
    this.loadingUpdate = true;
    this.errors = [];

    const license: ILicense = new License({
      id: Number(this.formId?.value),
      product: new Product({ id: Number(this.formProductId?.value) }),
      alias: this.formAlias?.value,
      name: this.formName?.value,
      type: this.formType?.value,
      disabled: Number(!this.formStatus?.value),
    });

    try {
      const licenseResponse: string = await this.licenseService.update(license);

      // Añadir un registro de actualización de la licencia.
      try {
        await this.logService.create(
          new Log({
            description: `Ha actualizado la licencia '${this.license.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de actualización de la licencia.'
          );
        }
      }

      this.snackBar.open(licenseResponse, '', {
        duration: 5000,
      });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loadingUpdate = false;
  }
}
